/** Shared scheduler. Each cached view acknowledges its own domain revisions. */
export function createTableRefresh({
  window: win = typeof window === 'undefined' ? null : window,
  document: doc = typeof document === 'undefined' ? null : document,
  timers = globalThis,
  now = Date.now,
} = {}) {
  const storageKey = 'hr:data-changed:v1';
  const revisions = new Map();
  const entries = new Set();
  let timer = null;
  let queued = null;
  const visible = () => (!doc || doc.visibilityState !== 'hidden') && win?.navigator?.onLine !== false;
  const snapshot = (domains) => domains.map((domain) => revisions.get(domain) || 0);
  const changed = (entry) => snapshot(entry.domains).some((value, i) => value !== entry.seen[i]);
  const ready = (entry) => !entry.disposed && visible() && entry.enabled() && !entry.busy();

  async function run(entry) {
    if (!ready(entry) || entry.running || now() < entry.retryAt) return;
    if (!entry.pending && entry.loadedAt !== null && !changed(entry) && now() - entry.loadedAt < entry.ttl) return;
    const seen = snapshot(entry.domains);
    entry.running = true;
    try {
      const result = await entry.refresh();
      if (result === false) throw new Error('refresh.failed');
      // null: a newer read replaced this one, so its snapshot is not confirmed.
      if (result === null) {
        entry.pending = true;
        return;
      }
      entry.pending = false;
      entry.seen = seen;
      entry.loadedAt = now();
      entry.failures = 0;
      entry.retryAt = 0;
    } catch {
      entry.pending = false;
      entry.failures += 1;
      entry.retryAt = now() + Math.min(60_000, 5_000 * (2 ** Math.min(entry.failures - 1, 4)));
    } finally {
      entry.running = false;
      // A mutation arriving during the request must remain pending.
      if (!entry.disposed && (entry.pending || (!entry.failures && changed(entry)))) schedule();
    }
  }
  const refresh = () => Promise.all([...entries].map(run));
  function schedule() {
    if (queued !== null) return;
    queued = timers.setTimeout(() => { queued = null; void refresh(); }, 100);
  }
  function invalidate(domains) {
    for (const domain of domains) revisions.set(domain, (revisions.get(domain) || 0) + 1);
    schedule();
  }
  const onStorage = (event) => {
    if (event.key !== storageKey || !event.newValue) return;
    try {
      const domains = JSON.parse(event.newValue).domains;
      if (Array.isArray(domains)) invalidate(domains.filter((value) => typeof value === 'string'));
    } catch { /* Ignore malformed or obsolete tab messages. */ }
  };
  const onVisible = () => { if (visible()) schedule(); };
  function stop() {
    if (timer !== null) timers.clearInterval(timer);
    if (queued !== null) timers.clearTimeout(queued);
    timer = queued = null;
    win?.removeEventListener('focus', onVisible);
    win?.removeEventListener('online', onVisible);
    win?.removeEventListener('storage', onStorage);
    doc?.removeEventListener('visibilitychange', onVisible);
  }
  function subscribe(refreshView, { domains, enabled = () => true, busy = () => false, ttl = 60_000 } = {}) {
    const entry = { domains, enabled, busy, ttl, refresh: refreshView, seen: snapshot(domains),
      loadedAt: null, running: false, pending: false, failures: 0, retryAt: 0, disposed: false };
    entries.add(entry);
    if (win && timer === null) {
      timer = timers.setInterval(() => { void refresh(); }, 5_000);
      win.addEventListener('focus', onVisible);
      win.addEventListener('online', onVisible);
      win.addEventListener('storage', onStorage);
      doc?.addEventListener('visibilitychange', onVisible);
    }
    return {
      check: () => run(entry),
      checkpoint: () => snapshot(domains),
      markLoaded: (seen = snapshot(domains)) => {
        if (entry.running) return;
        entry.pending = false;
        entry.seen = seen;
        entry.loadedAt = now();
        entry.failures = 0;
        entry.retryAt = 0;
      },
      stop: () => { entry.disposed = true; entries.delete(entry); if (!entries.size) stop(); },
    };
  }
  function notify(domains) {
    invalidate(domains);
    try { win?.localStorage.setItem(storageKey, JSON.stringify({ domains, nonce: `${now()}:${Math.random()}` })); }
    catch { /* Same-tab updates work when storage is unavailable. */ }
  }
  return { subscribe, notify, refresh };
}

const tableRefresh = createTableRefresh();
export const subscribeTableRefresh = (...args) => tableRefresh.subscribe(...args);
export const notifyTableDataChanged = (domains) => tableRefresh.notify(domains);

/** POST is also used for reads: only explicit write endpoints invalidate data. */
export function apiMutationDomains(config = {}) {
  if (!['post', 'put', 'patch', 'delete'].includes(String(config.method || '').toLowerCase())) return [];
  const path = String(config.url || '').split('?')[0].replace(/^.*\/api\/v1\//, '').replace(/^\//, '');
  if (path === 'people-tags') {
    let body = config.data || {};
    if (typeof body === 'string') { try { body = JSON.parse(body); } catch { return []; } }
    return ['apply','save'].includes(body.action) ? ['people-tags','employees','candidates','organization','workflow'] : [];
  }
  if (!/(?:^|\/)(?:manage|create|import|upload|delete|restore|submit|update|cancel|decide|confirm|revoke|complete|complete-override|save|generate)$/.test(path)) return [];
  const module = path.split('/')[0];
  const dependencies = {
    employees: ['employees', 'compensation', 'payroll', 'workflow', 'dashboard'],
    users: ['employees', 'workflow'],
    candidates: ['candidates', 'employees', 'dashboard'],
    departments: ['organization', 'employees', 'candidates', 'compensation', 'payroll', 'dashboard'],
    positions: ['organization', 'employees', 'candidates', 'dashboard'],
    locations: ['organization', 'employees', 'candidates'],
    'sub-departments': ['organization', 'employees'],
    teams: ['organization', 'employees'],
    grades: ['organization', 'employees', 'compensation'],
    countries: ['organization', 'employees', 'candidates'],
    'employment-reference': ['organization', 'employees', 'compensation'],
    compensation: ['compensation', 'payroll'],
    attendance: ['attendance', 'payroll', 'employees', 'dashboard'],
    'time-off': ['attendance', 'payroll', 'employees', 'workflow', 'dashboard'],
    'work-schedule': ['attendance', 'payroll', 'employees'],
    documents: ['documents'],
    'employee-documents': ['documents', 'employees'],
    'employee-reports': ['employee-reports'],
    plans: ['workflow', 'dashboard'],
    appraisals: ['workflow', 'dashboard'],
    jd: ['job-descriptions'],
    'jd-templates': ['job-descriptions'],
    complaints: ['complaints'],
  };
  return dependencies[module] || [];
}
